import type { Fatura } from '../../types';

/**
 * Converte um UC no formato espelho (zero-padded) para o formato definitivo da Equatorial.
 * Ex: "0000016138801217" → "161.388.012-17"
 * Padrão: após remover zeros à esquerda, 11 dígitos → XXX.XXX.XXX-XX
 */
export function formatUcEspelho(uc: string): string {
  const stripped = uc.replace(/^0+/, '');
  if (stripped.length < 4 || /[.\-]/.test(uc)) return uc; // já formatado ou muito curto
  // Últimos 2 dígitos = dígito verificador
  const check = stripped.slice(-2);
  const main = stripped.slice(0, -2);
  // Quebra o main em grupos de 3 da direita para a esquerda
  const parts: string[] = [];
  let rest = main;
  while (rest.length > 3) {
    parts.unshift(rest.slice(-3));
    rest = rest.slice(0, -3);
  }
  if (rest) parts.unshift(rest);
  return `${parts.join('.')}-${check}`;
}

export function isPdfEspelho(fatura: Fatura): boolean {
  return fatura.tipo_pdf_fatura === 'espelho';
}

const MES_NUM: Record<string, number> = {
  JAN: 1, FEV: 2, MAR: 3, ABR: 4, MAI: 5, JUN: 6,
  JUL: 7, AGO: 8, SET: 9, OUT: 10, NOV: 11, DEZ: 12,
};

function parseMesReferencia(mesRef: string): { mes: number; ano: number } | null {
  const m = (mesRef || '').trim().toUpperCase().match(/^([A-Z]{3})[\/\-]?(\d{4})$/);
  if (!m) return null;
  const mes = MES_NUM[m[1]] ?? 0;
  const ano = parseInt(m[2], 10);
  return mes > 0 ? { mes, ano } : null;
}

/**
 * Determina se o botão "Substituir PDF definitivo" deve estar disponível.
 * - ABR/2026 e anteriores: sempre permite (independe de ser espelho ou definitiva)
 * - MAI/2026 em diante: só permite se a fatura ainda não tem boleto gerado
 */
export function canSubstitutePdf(fatura: Fatura): boolean {
  const parsed = parseMesReferencia(fatura.mes_referencia || '');

  const isOldFatura = !parsed || parsed.ano < 2026 || (parsed.ano === 2026 && parsed.mes <= 4);

  if (isOldFatura) return true;

  const STATUSES_COM_BOLETO = [
    'Com boleto calculado',
    'Cobrança enviada',
  ];
  return !STATUSES_COM_BOLETO.includes(fatura.status_fatura || '');
}

/**
 * Fatura da distribuidora com valor total R$ 0,00.
 * A Equatorial não disponibiliza a fatura definitiva quando o valor é zero
 * (confirmado com o suporte da distribuidora): só existe o espelho. Sem esta
 * exceção a fatura ficaria presa para sempre em "A enviar", porque o bloqueio
 * de espelho exige um PDF definitivo que nunca vai existir.
 */
export function isFaturaZerada(fatura: Fatura): boolean {
  const total = Number(fatura.valor_fatura_distribuidora ?? 0);
  return !isNaN(total) && Math.abs(total) < 0.005;
}

/**
 * Regra única do bloqueio de envio por espelho: vale para espelho de fatura COM
 * valor (o cliente não teria como pagar — o espelho não traz PIX nem código de
 * barras), não para fatura zerada, onde não há nada a cobrar e o envio é apenas
 * a comunicação informativa do mês.
 *
 * `ucPermiteEspelho` é a exceção por cliente (`unidades_consumidoras.
 * permite_envio_espelho`, migration 20260914120000): tem cliente que precisa
 * receber o espelho e paga a distribuidora por fora do código de barras — o
 * caso que abriu a exceção foi a Prefeitura de Morrinhos, contrato de pregão
 * cujas faturas são enviadas só como comunicação. Vem da UC, não da fatura: é
 * característica de quem recebe, não do mês. Quem chama sem a opção continua
 * com a regra travada — o default é bloquear.
 */
export function bloqueiaEnvioPorEspelho(
  fatura: Fatura,
  opcoes?: { ucPermiteEspelho?: boolean | null },
): boolean {
  if (opcoes?.ucPermiteEspelho === true) return false;
  return isPdfEspelho(fatura) && !isFaturaZerada(fatura);
}
